import { useEffect, useMemo, useState } from "react";

export const HUB_OPERATION_PAGE_SIZES = [10, 20, 50, 100] as const;
export type HubOperationPageSize = (typeof HUB_OPERATION_PAGE_SIZES)[number];

export function hubOperationPageCount(total: number, pageSize: number): number {
	if (total <= 0) return 1;
	return Math.ceil(total / pageSize);
}

export function hubOperationPaginateSlice<T>(rows: T[], page: number, pageSize: number): T[] {
	if (rows.length === 0) return rows;
	const totalPages = hubOperationPageCount(rows.length, pageSize);
	const safePage = Math.min(Math.max(1, page), totalPages);
	const start = (safePage - 1) * pageSize;
	return rows.slice(start, start + pageSize);
}

export function useHubOperationPagination(total: number, resetKey: string) {
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState<HubOperationPageSize>(10);

	useEffect(() => {
		setPage(1);
	}, [resetKey]);

	useEffect(() => {
		setPage(1);
	}, [pageSize]);

	const totalPages = hubOperationPageCount(total, pageSize);
	const safePage = Math.min(Math.max(1, page), totalPages);

	useEffect(() => {
		if (page !== safePage) setPage(safePage);
	}, [page, safePage]);

	const sliceRows = useMemo(
		() => <T,>(rows: T[]) => hubOperationPaginateSlice(rows, safePage, pageSize),
		[safePage, pageSize],
	);

	return {
		page: safePage,
		setPage,
		pageSize,
		setPageSize,
		totalPages,
		sliceRows,
	};
}
